import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';

const ACCENT = '#10B981';
const CYAN = '#06B6D4';
const PURPLE = '#8B5CF6';

const CARD: React.CSSProperties = {
  background: 'var(--card)',
  border: '1px solid var(--border)',
  borderRadius: 20,
  padding: 28,
};

export function GeoPage() {
  const { user } = useAuth();
  const [scanned, setScanned] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [bizName, setBizName] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const { data } = await api.get<{ business: { business_name: string } | null }>('/business/me');
        if (data.business?.business_name) setBizName(data.business.business_name);
      } catch {
        /* demo */
      }
    })();
  }, []);

  function runScan() {
    setScanning(true);
    window.setTimeout(() => {
      setScanned(true);
      setScanning(false);
    }, 3000);
  }

  const pillBase: React.CSSProperties = {
    border: '1px solid var(--border)',
    borderRadius: 999,
    padding: '8px 16px',
    background: 'var(--card)',
    fontSize: 12,
  };
  const geoScore = 72;
  const geoColor = geoScore >= 70 ? '#10B981' : geoScore >= 40 ? '#F59E0B' : '#EF4444';
  const geoGlow =
    geoScore >= 70 ? 'rgba(16,185,129,0.3)' : geoScore >= 40 ? 'rgba(245,158,11,0.3)' : 'rgba(239,68,68,0.3)';

  return (
    <div className="odc-page relative z-10" style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 24,
        }}
      >
        <div style={{ flex: '1 1 280px', minWidth: 0 }}>
          <span
            style={{
              display: 'inline-flex',
              padding: '5px 14px',
              borderRadius: 999,
              border: '1px solid rgba(6,182,212,0.3)',
              background: 'rgba(6,182,212,0.08)',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.14em',
              color: CYAN,
              textTransform: 'uppercase',
            }}
          >
            GEO &amp; AEO · BETA
          </span>
          <h1
            style={{
              margin: '12px 0 0',
              fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)',
              fontWeight: 800,
              color: 'var(--text)',
            }}
          >
            AI Visibility Score
          </h1>
          <p style={{ margin: '12px 0 0', color: 'var(--muted)', fontSize: 15, maxWidth: 540, lineHeight: 1.6 }}>
            Track how often your brand appears in ODC AI answers
          </p>
          {bizName || user?.name ? (
            <p style={{ margin: '8px 0 0', color: 'var(--muted)', fontSize: 12 }}>
              {bizName ? `Business: ${bizName}` : null}
              {user?.name ? ` · ${user.name}` : null}
            </p>
          ) : null}
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ ...pillBase, fontWeight: 700, color: ACCENT }}>🤖 ODC AI</span>
          <span style={{ ...pillBase, fontWeight: 700, color: CYAN }}>✨ ODC AI</span>
          <span style={{ ...pillBase, fontWeight: 700, color: PURPLE }}>🔍 ODC AI</span>
        </div>
      </div>

      <div className="odc-card" style={{ ...CARD, textAlign: 'center', padding: 48, transform: 'none' }}>
        {!scanned ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24 }}>
            <div style={{ position: 'relative', width: 180, height: 180, margin: '0 auto' }}>
              <div
                style={{
                  position: 'absolute',
                  inset: -4,
                  borderRadius: '50%',
                  boxShadow: `0 0 30px ${geoGlow}`,
                  border: `2px solid ${geoColor}`,
                  opacity: 0.3,
                }}
              />
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  background: `conic-gradient(${geoColor} 0deg, rgba(255,255,255,0.06) 0deg)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <div
                  style={{
                    width: '70%',
                    height: '70%',
                    borderRadius: '50%',
                    background: 'var(--bg2)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid rgba(255,255,255,0.05)',
                  }}
                >
                  <span style={{ fontSize: 52, fontWeight: 900, color: '#ffffff', fontFamily: "'Space Grotesk',sans-serif" }}>
                    —
                  </span>
                  <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.65)' }}>/100</span>
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>Score</span>
                </div>
              </div>
            </div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>Your AI Visibility Score</h2>
            <p style={{ margin: 0, color: 'var(--muted)', fontSize: 14, maxWidth: 400, lineHeight: 1.6 }}>
              Find out how often ODC AI mentions your business
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
              {['🤖 ODC AI: —%', '✨ ODC AI: —%', '🔍 ODC AI: —%'].map((t) => (
                <span
                  key={t}
                  style={{
                    border: '1px solid var(--border)',
                    borderRadius: 999,
                    padding: '8px 20px',
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'rgba(255,255,255,0.7)',
                  }}
                >
                  {t}
                </span>
              ))}
            </div>
            <button
              type="button"
              className="odc-btn-primary"
              style={{ padding: '14px 36px', fontSize: 15, marginTop: 8 }}
              disabled={scanning}
              onClick={runScan}
            >
              {scanning ? 'Scanning... ⏳' : '🚀 Run First Scan'}
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24 }}>
            <div style={{ position: 'relative', width: 180, height: 180, margin: '0 auto' }}>
              <div
                style={{
                  position: 'absolute',
                  inset: -4,
                  borderRadius: '50%',
                  boxShadow: `0 0 30px ${geoGlow}`,
                  border: `2px solid ${geoColor}`,
                  opacity: 0.4,
                }}
              />
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  background: `conic-gradient(${geoColor} ${geoScore * 3.6}deg, rgba(255,255,255,0.06) 0deg)`,
                  boxShadow: `0 0 20px ${geoGlow}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <div
                  style={{
                    width: '70%',
                    height: '70%',
                    borderRadius: '50%',
                    background: 'var(--bg2)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid rgba(255,255,255,0.05)',
                  }}
                >
                  <span style={{ fontSize: 52, fontWeight: 900, color: '#ffffff', fontFamily: "'Space Grotesk',sans-serif" }}>
                    {geoScore}
                  </span>
                  <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.65)' }}>/100</span>
                </div>
              </div>
            </div>
            <p style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#ffffff' }}>{geoScore}/100 · Good Visibility</p>
            <span className="odc-badge odc-badge-green" style={{ fontSize: 12, fontWeight: 700 }}>
              Good
            </span>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ ...pillBase, fontWeight: 600, color: ACCENT }}>🤖 ODC AI: 68%</span>
              <span style={{ ...pillBase, fontWeight: 600, color: CYAN }}>✨ ODC AI: 78%</span>
              <span style={{ ...pillBase, fontWeight: 600, color: PURPLE }}>🔍 ODC AI: 70%</span>
            </div>
            <p style={{ margin: 0, fontSize: 11, color: 'var(--muted)', fontStyle: 'italic', maxWidth: 420 }}>
              * Demo scores. Real scores require GEO Guardian agent + connected accounts
            </p>
            <button
              type="button"
              className="odc-btn-ghost"
              style={{ marginTop: 8 }}
              onClick={() => setScanned(false)}
            >
              Scan again
            </button>
          </div>
        )}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 20,
        }}
        className="odc-geo-how-grid"
      >
        {(
          [
            { icon: '🔍', title: 'We Ask AI', body: 'We send your business keywords to ODC AI' },
            { icon: '📊', title: 'We Measure', body: 'Track how often your brand is cited vs competitors' },
            { icon: '🚀', title: 'We Optimize', body: 'GEO Guardian agent auto-improves your content for AI citations' },
          ] as const
        ).map((c) => (
          <div key={c.title} style={CARD}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: 'rgba(16,185,129,0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 20,
              }}
            >
              {c.icon}
            </div>
            <h3 style={{ margin: '12px 0 0', fontWeight: 700, fontSize: 16, color: 'var(--text)' }}>{c.title}</h3>
            <p style={{ margin: '8px 0 0', color: 'var(--muted)', fontSize: 14, lineHeight: 1.55 }}>{c.body}</p>
          </div>
        ))}
      </div>
      <style>{`
        @media (max-width: 900px) {
          .odc-geo-how-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <div className="odc-card" style={{ ...CARD, transform: 'none' }}>
        <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>Platform Breakdown</h2>
        <div style={{ overflowX: 'auto' }}>
          <table className="odc-table" style={{ width: '100%', minWidth: 520 }}>
            <thead>
              <tr>
                <th>PLATFORM</th>
                <th>QUERIES TESTED</th>
                <th>BRAND MENTIONS</th>
                <th>SCORE</th>
                <th>STATUS</th>
              </tr>
            </thead>
            <tbody>
              {(
                [
                  { plat: '🤖 ODC AI', q: '50 queries', m: scanned ? '12' : '—', s: scanned ? '68%' : '—', kind: 'data' as const },
                  { plat: '✨ ODC AI', q: '50 queries', m: scanned ? '15' : '—', s: scanned ? '78%' : '—', kind: 'data' as const },
                  { plat: '🔍 ODC AI', q: '30 queries', m: scanned ? '8' : '—', s: scanned ? '70%' : '—', kind: 'data' as const },
                  { plat: '🤖 ODC AI', q: 'Coming Soon', m: '—', s: '—', kind: 'soon' as const },
                  { plat: '💬 ODC AI', q: 'Coming Soon', m: '—', s: '—', kind: 'soon' as const },
                ] as const
              ).map((row) => (
                <tr key={row.plat}>
                  <td style={{ fontWeight: 600 }}>{row.plat}</td>
                  <td>{row.q}</td>
                  <td>{row.m}</td>
                  <td>{row.s}</td>
                  <td>
                    {row.kind === 'soon' ? (
                      <span className="odc-badge odc-badge-amber" style={{ fontSize: 10 }}>
                        Coming Soon
                      </span>
                    ) : scanned ? (
                      <span className="odc-badge odc-badge-green" style={{ fontSize: 10 }}>
                        Good
                      </span>
                    ) : (
                      <span className="odc-badge" style={{ fontSize: 10, background: 'var(--card2)', color: 'var(--muted)' }}>
                        Scan Required
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="odc-card" style={{ ...CARD, transform: 'none' }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>🏆 Quick Wins to Boost Your Score</h2>
        <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--muted)' }}>Implement these to get mentioned in AI answers</p>
        <ul style={{ margin: '16px 0 0', padding: 0, listStyle: 'none' }}>
          {(
            [
              { n: 1, title: 'Add FAQ Schema', body: 'Add FAQ section to your website with structured JSON-LD data' },
              { n: 2, title: 'Get Media Mentions', body: 'Get mentioned on 5+ authoritative news/blog websites' },
              { n: 3, title: 'Complete GMB Profile', body: 'Keep Google My Business 100% complete with fresh posts' },
              { n: 4, title: 'Enable GEO Guardian', body: 'Turn on Agent #81 in your Agents page' },
              { n: 5, title: 'Weekly Blog Posts', body: 'Publish weekly blogs with question-answer format content' },
            ] as const
          ).map((tip, idx) => (
            <li
              key={tip.n}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '12px 0',
                borderBottom: idx < 4 ? '1px solid var(--border)' : undefined,
              }}
            >
              <span
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  background: 'rgba(16,185,129,0.12)',
                  color: ACCENT,
                  fontSize: 11,
                  fontWeight: 800,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {tip.n}
              </span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)' }}>{tip.title}</div>
                <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4, lineHeight: 1.5 }}>{tip.body}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div
        style={{
          border: '1px solid rgba(16,185,129,0.3)',
          borderRadius: 20,
          padding: 40,
          background: 'rgba(16,185,129,0.04)',
          textAlign: 'center',
        }}
      >
        <h3 style={{ margin: '0 0 12px', fontSize: 22, fontWeight: 800, color: 'var(--text)' }}>Enable GEO Guardian Agent</h3>
        <p style={{ margin: '0 0 24px', color: 'var(--muted)', fontSize: 15, lineHeight: 1.6, maxWidth: 520, marginLeft: 'auto', marginRight: 'auto' }}>
          Auto-optimize your content for ODC AI citations. Set once, works forever.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
          <Link to="/agents" className="odc-btn-primary" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
            ⚡ Enable Agent #81
          </Link>
          <Link to="/simulator" className="odc-btn-ghost" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
            🧪 Test Campaign First
          </Link>
        </div>
      </div>
    </div>
  );
}
